import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CrosshairIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { getClientId } from "../lib/clientId";
import { addTrackedWallet, watchCreatorAlert, fetchTopHolder } from "../lib/api";

export const TrackDevWalletButton = ({ creator, symbol }) => {
  const { requireAuth } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!creator) return null;

  const go = async () => {
    if (!requireAuth("track this dev wallet")) return;
    setBusy(true);
    const cid = getClientId();
    const label = `$${(symbol || "DEV").replace(/^\$/, "")} dev`;
    try {
      await addTrackedWallet(cid, creator, label);
      try { await watchCreatorAlert(cid, creator, symbol); } catch { /* alerts are best-effort */ }
      toast.success(`Tracking ${label} — added to Smart Money + launch alerts`);
      navigate("/smart-money");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't track this wallet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid="track-dev-wallet-btn"
      onClick={go}
      disabled={busy}
      title="Add this deployer to Smart Money and get pinged on their future launches"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-neon text-black hover:bg-neon/90 transition-colors disabled:opacity-50"
    >
      <CrosshairIcon size={14} weight="bold" /> {busy ? "Tracking…" : "Track Dev Wallet"}
    </button>
  );
};

export const TrackTopHolderButton = ({ mint, symbol }) => {
  const { requireAuth } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!mint) return null;

  const go = async () => {
    if (!requireAuth("track the top holder")) return;
    setBusy(true);
    try {
      const th = await fetchTopHolder(mint);
      if (!th?.available || !th.owner) {
        toast.error("No trackable top-holder found — the biggest wallets are locked LP / exchange accounts");
        return;
      }
      const label = `$${(symbol || "TOP").replace(/^\$/, "")} top holder`;
      await addTrackedWallet(getClientId(), th.owner, label);
      toast.success(`Tracking ${label} — added to Smart Money`);
      navigate("/smart-money");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't track the top holder");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid="track-top-holder-btn"
      onClick={go}
      disabled={busy}
      title="Add this token's largest real holder wallet to Smart Money (skips locked LP / exchange accounts)"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-neon/50 text-neon hover:bg-neon/10 transition-colors disabled:opacity-50"
    >
      <UsersThreeIcon size={14} weight="bold" /> {busy ? "Finding…" : "Track Top Holder"}
    </button>
  );
};
